import { useEffect, useState } from 'react'
import Week from './Calendar/Week'
import Day from './Calendar/Day'
import Month from './Calendar/Month'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { useQuery } from '@tanstack/react-query'
import { DAIRequest } from 'src/utils/DAIRequest'
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns'
import Loading from 'src/DAIComponents/Common/Loading'
import useAuthContext from 'src/hooks/useAuthContext'
import { VIEWS, CalendarEvent } from './AppointmentTypes'

const getViewRange = (view: string, date: Date) => {
  switch (view) {
    case VIEWS.DAY:
      return {
        start: startOfDay(date).toISOString(),
        end: endOfDay(date).toISOString(),
      }
    case VIEWS.WEEK:
      return {
        start: startOfWeek(date, { weekStartsOn: 0 }).toISOString(), // Sunday
        end: endOfWeek(date, { weekStartsOn: 0 }).toISOString(), // Saturday
      }
    case VIEWS.MONTH:
      return {
        start: startOfMonth(date).toISOString(),
        end: endOfMonth(date).toISOString(),
      }
    default:
      return {
        start: startOfWeek(date, { weekStartsOn: 0 }).toISOString(),
        end: endOfWeek(date, { weekStartsOn: 0 }).toISOString(),
      }
  }
}

export default function Appointments() {
  const [view, setView] = useState(VIEWS.WEEK) // Default view is Week
  const [currentDate, setCurrentDate] = useState(new Date())
  const { start, end } = getViewRange(view, currentDate)
  const { notifyUserToSetupIntegration, isEmailIntegrationComplete } =
    useAuthContext()

  useEffect(() => {
    notifyUserToSetupIntegration()
  }, [])

  const { data: events, isLoading: isGetEventsLoading } = useQuery<
    CalendarEvent[]
  >({
    queryKey: ['calendar-events', view, currentDate],
    queryFn: async () => {
      return (
        await DAIRequest('GET', '/api/v1/unipile/getCalendarEvents/:to/:from', {
          params: { from: start, to: end },
        })
      ).data
    },
    enabled: !!currentDate && isEmailIntegrationComplete, // only query when date is set
  })

  // Format the header date dynamically
  const formatHeaderDate = (date: Date) => {
    return date.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    })
  }

  // Navigate to Previous, Next, and Today
  const navigate = (direction: 'prev' | 'next' | 'today') => {
    const newDate = new Date(currentDate)
    if (direction === 'prev') {
      if (view === VIEWS.DAY) {
        newDate.setDate(newDate.getDate() - 1)
      } else if (view === VIEWS.WEEK) {
        newDate.setDate(newDate.getDate() - 7)
      } else {
        newDate.setMonth(newDate.getMonth() - 1)
      }
    } else if (direction === 'next') {
      if (view === VIEWS.DAY) {
        newDate.setDate(newDate.getDate() + 1)
      } else if (view === VIEWS.WEEK) {
        newDate.setDate(newDate.getDate() + 7)
      } else {
        newDate.setMonth(newDate.getMonth() + 1)
      }
    } else {
      newDate.setTime(Date.now()) // Go to today's date
    }
    setCurrentDate(newDate)
  }

  // Render the correct component based on the selected view
  const renderView = () => {
    switch (view) {
      case VIEWS.DAY:
        return <Day />
      case VIEWS.WEEK:
        return <Week currentDate={currentDate} events={events ?? []} />
      case VIEWS.MONTH:
        return <Month />
      default:
        return <Week currentDate={currentDate} events={events ?? []} />
    }
  }

  if (isGetEventsLoading) {
    return <Loading full={true} />
  }

  function onAdd(): void {
    throw new Error('Function not implemented.')
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header Navigation */}
      <header className="flex flex-none items-center justify-between border-b border-gray-200 px-6 py-4">
        <h1 className="text-base font-semibold text-gray-900">
          <time dateTime={currentDate.toISOString()}>
            {formatHeaderDate(currentDate)}
          </time>
        </h1>
        <div className="flex items-center">
          {/* Navigation buttons */}
          <div className="relative flex items-center rounded-md bg-white shadow-xs md:items-stretch">
            <button
              onClick={() => navigate('prev')}
              type="button"
              className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
            >
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <button
              onClick={() => navigate('today')}
              type="button"
              className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
            >
              Today
            </button>
            <button
              onClick={() => navigate('next')}
              type="button"
              className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>

          {/* View Options */}
          <div className="hidden md:ml-4 md:flex md:items-center">
            {/* <Menu as="div" className="relative">
              <MenuButton
                type="button"
                className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50"
              >
                {view.charAt(0).toUpperCase() + view.slice(1)} view
                <ChevronDownIcon
                  className="-mr-1 size-5 text-gray-400"
                  aria-hidden="true"
                />
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-50 mt-3 w-36 origin-top-right overflow-hidden rounded-md bg-white ring-1 shadow-lg ring-black/5 focus:outline-hidden"
              >
                <div className="py-1">
                  {Object.values(VIEWS).map(v => (
                    <MenuItem key={v}>
                      <button
                        onClick={() => setView(v)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:text-gray-900"
                      >
                        {v.charAt(0).toUpperCase() + v.slice(1)} view
                      </button>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Menu> */}
            {/* <div className="ml-6 h-6 w-px bg-gray-300" /> */}
            {/* <DAIButton text='Add Event' onClick={onAdd} /> */}
          </div>
        </div>
      </header>

      {/* Calendar View - dynamically renders Day, Week, or Month */}
      <div className="flex-1 overflow-y-auto">{renderView()}</div>
    </div>
  )
}
